import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $trimTextContentFromAnchor } from '@lexical/selection'
import { $restoreEditorState } from '@lexical/utils'
import { $getSelection, $isRangeSelection, RootNode } from 'lexical'
import React from 'react'
import type { TextEditorProps } from '../text-editor.types'

type TProps = Pick<TextEditorProps, 'maxLength'>

export const useMaxLength = ({ maxLength }: TProps) => {
  const [editor] = useLexicalComposerContext()

  React.useEffect(() => {
    if (maxLength == null) return

    let lastRestoredEditorState: ReturnType<typeof editor.getEditorState> | null = null

    return editor.registerNodeTransform(RootNode, (rootNode) => {
      const selection = $getSelection()
      if (!$isRangeSelection(selection) || !selection.isCollapsed()) return

      const prevEditorState = editor.getEditorState()
      const prevTextContentSize = prevEditorState.read(() => rootNode.getTextContentSize())
      const textContentSize = rootNode.getTextContentSize()

      if (prevTextContentSize === textContentSize) return

      const delCount = textContentSize - maxLength
      if (delCount <= 0) return

      // If we were already at the limit, restore the previous state instead of
      // trimming (handles pastes and IME that jump well past the cap).
      if (prevTextContentSize === maxLength && lastRestoredEditorState !== prevEditorState) {
        lastRestoredEditorState = prevEditorState
        $restoreEditorState(editor, prevEditorState)
      } else {
        $trimTextContentFromAnchor(editor, selection.anchor, delCount)
      }
    })
  }, [editor, maxLength])
}
